import { useFormik } from "formik";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  UpdateFinanceCategoryRequestDTO,
  usePermission,
  useToast,
  useUpdateFinanceCategoryMutation,
} from "@/hooks";
import { FinanceCategoryValidationSchema } from "@/shared";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FinanceCategoryFinanceAccount } from "@/types";
import { DisplayError } from "@/utils";
import { defaultEntriesCategories } from "@/helpers";

type FormCreateFinanceCategoryProps = {
  handleClose: () => void;
  category: FinanceCategoryFinanceAccount;
};

export function FormUpdateFinanceCategory({
  handleClose,
  category,
}: FormCreateFinanceCategoryProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { checkPermission } = usePermission()

  const initialValues = {
    id: category.id,
    name: defaultEntriesCategories.includes(category.name.toLowerCase()) ? t(`LABEL.${category.name.toLowerCase()}`) : category.name,
  };

  const { mutateAsync, isLoading } = useUpdateFinanceCategoryMutation();
  const { handleSubmit, getFieldProps, errors, touched } = useFormik({
    initialValues,
    validationSchema: FinanceCategoryValidationSchema(),
    onSubmit: async (categoryData) => {
      handleCreateCategorySubmit(categoryData);
    },
  });

  async function handleCreateCategorySubmit(
    categoryData: UpdateFinanceCategoryRequestDTO
  ) {
    try {
      mutateAsync(categoryData, {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message")),
            variant: "destructive",
          });
        },
      });

      handleClose();
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message")),
        variant: "destructive",
      });
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-10 px-4 pb-8">
      <div className="flex flex-col gap-2">
        <Label>{t("LABEL.name")}</Label>
        <div className="space-y-1">
          <Input
            isInvalid={touched.name && errors.name}
            isValid={touched.name && !errors.name}
            {...getFieldProps("name")}
          />
          {touched.name && errors.name ? (
            <span className="text-red-500 text-sm min-h-3">{errors.name}</span>
          ) : (
            <div className="min-h-3"></div>
          )}
        </div>
      </div>
      <Button isLoading={isLoading} className="w-full" disabled={!checkPermission("category", "delete categories")}>
        {t("LABEL.save")}
      </Button>
    </form>
  );
}
